import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@c2fo/liquidity';
import { BoltIcon } from '@c2fo/liquidity/icons';
import { useLocationsState } from '../../contexts';
import { StorageTypeChip } from '../StorageTypeChip';
import { UriLink } from '../UriLink';
import { BackLink } from '../BackLink';
import { Card } from '../Card';
import { CardHeader } from '../CardHeader';
import { DetailField } from '../DetailField';
import { EditButton } from '../EditButton';
import { FieldsContainer } from '../FieldsContainer';
import { EntityLink } from '../EntityLink';
import { NotFoundCard } from '../NotFoundCard';
import { PageContainer } from '../PageContainer';
import { ConnectivityState } from '../../api/shared';
import { LoadingOverlay } from '../LoadingSpinner';
import { formatDuration, getConnectivityStatusLabel, getConnectivityStatusColor } from '../../utils';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';
import type { TestConnectivityResponse } from '../../api/Locations';

export const LocationView: React.FC = () => {
  const { locationUid } = useParams<{ locationUid: string }>();
  const navigate = useNavigate();
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectivityResponse | null>(null);
  const {
    currentLocation: location,
    isLoadingLocation: isLoading,
    fetchLocation,
    clearCurrentLocation,
    testLocation,
  } = useLocationsState();

  const handleTestConnectivity = async () => {
    if (!location) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await testLocation(location.name);
      setTestResult(result);
    } catch {
      setTestResult({
        connectivityState: ConnectivityState.CONNECTIVITY_STATE_DISCONNECTED,
        message: 'Test failed',
      });
    } finally {
      setIsTesting(false);
    }
  };

  useEffect(() => {
    if (locationUid) {
      // Construct full path: locations/{locationUid}
      fetchLocation(`locations/${locationUid}`);
    }

    return () => {
      clearCurrentLocation();
    };
  }, [locationUid, fetchLocation, clearCurrentLocation]);

  if (isLoading) {
    return <LoadingOverlay message="Loading location..." />;
  }

  if (!location) {
    return (
      <PageContainer>
        <BackLink to="/file-delivery/locations" label="Back to Locations" />
        <NotFoundCard message="Location not found or could not be loaded." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to="/file-delivery/locations" label="Back to Locations" />

      <Card>
        <CardHeader
          chip={<StorageTypeChip storageType={location.storageType} />}
          title={location.displayName}
          description={location.description}
          actions={
            <>
              <Button
                variant="secondary"
                className="rounded-md"
                size="sm"
                onClick={handleTestConnectivity}
                disabled={isTesting}
              >
                <span className="flex items-center gap-1">
                  <BoltIcon />
                  {isTesting ? 'Testing...' : 'Test Connectivity'}
                </span>
              </Button>
              <EditButton onClick={() => navigate(`/file-delivery/${location.name}/edit`)} />
            </>
          }
        />
        {testResult && (
          <div
            className={`px-6 py-3 border-b ${getConnectivityStatusColor(testResult.connectivityState).bg} ${getConnectivityStatusColor(testResult.connectivityState).border}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span
                  className={`text-sm font-medium ${getConnectivityStatusColor(testResult.connectivityState).text}`}
                >
                  {testResult.connectivityState === ConnectivityState.CONNECTIVITY_STATE_CONNECTED ? '✓' : '✗'}{' '}
                  {getConnectivityStatusLabel(testResult.connectivityState)}
                </span>
                {testResult.message && (
                  <span className={`text-sm ${getConnectivityStatusColor(testResult.connectivityState).text}`}>
                    — {testResult.message}
                  </span>
                )}
              </div>
              {testResult.latency && (
                <span className={`text-sm ${getConnectivityStatusColor(testResult.connectivityState).text}`}>
                  {formatDuration(testResult.latency)}
                </span>
              )}
            </div>
          </div>
        )}
        <FieldsContainer>
          <DetailField label="Display Name" value={location.displayName} />
          <DetailField label="URI" value={<UriLink uri={location.uri} truncate={false} showTooltip={false} />} mono />
          <DetailField
            label="Credential"
            value={
              location.credential ? (
                <EntityLink to={`/file-delivery/credentials/${location.credential.uid}`}>
                  {location.credential.displayName}
                </EntityLink>
              ) : (
                'None'
              )
            }
          />
          <DetailField label="UID" value={location.uid} mono />
          <DetailField label="Resource Name" value={location.name} mono />
          <DetailField
            label="Created"
            value={location.createTime ? formatDateWithTimezone(location.createTime) : undefined}
          />
          <DetailField
            label="Last Updated"
            value={location.updateTime ? formatDateWithTimezone(location.updateTime) : undefined}
          />
        </FieldsContainer>
      </Card>
    </PageContainer>
  );
};
